import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { SearchBar } from '../components/SearchBar';
import { Badge } from '../components/SubscriptionAppBadge';
import { TableFilter } from '../components/TableFilter';
import type { Filter } from '../components/TableFilter';
import { Toast } from '../components/Toast';
import type { ToastProps } from '../components/Toast';
import { Button } from '../components/SubscriptionAppButton';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { Pagination } from '../components/SubscriptionAppPagination';
import { Input } from '../components/SubscriptionAppInput';
import { useLoyaltyAudit } from '../contexts/LoyaltyAuditContext';
import { Edit, X, ChevronDown, AlertTriangle, Info, ArrowRight, Shield, ToggleLeft, ArrowUpDown, ArrowUp, ArrowDown, ChevronRight, Lock, Download } from 'lucide-react';
// Tier thresholds
const TIER_THRESHOLDS = [{
  name: 'Enthusiast',
  min: 0,
  max: 499
}, {
  name: 'Connoisseur',
  min: 500,
  max: 1999
}, {
  name: 'Sommelier',
  min: 2000,
  max: 4999
}, {
  name: 'Vintner',
  min: 5000,
  max: Infinity
}];
const getTierForPoints = (points: number): string => {
  for (const tier of TIER_THRESHOLDS) {
    if (points >= tier.min && points <= tier.max) return tier.name;
  }
  return 'Enthusiast';
};
// Format an ISO timestamp as a relative-time string for display.
// e.g. '2026-05-26T12:00:00Z' → '2 hours ago' (when now is 14:00 UTC)
const formatRelativeTime = (iso: string): string => {
  const then = new Date(iso).getTime();
  const now = new Date().getTime();
  const diffMs = now - then;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  if (diffWeeks < 4) return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`;
  if (diffMonths < 12) return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
  return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
};
const REASON_PRESETS = ['Event bonus', 'Program migration', 'Correction', 'Goodwill gesture', 'Promotional campaign', 'Other'];
const ADD_REASON_PRESETS = ['Cellar door visit', 'Wine club event', 'Tasting experience', 'Program migration', 'Correction', 'Goodwill gesture', 'Promotional campaign', 'Other'];
const REMOVE_REASON_PRESETS = ['Correction', 'Program migration', 'Points expiry', 'Refund adjustment', 'Order cancellation', 'Other'];
// Mock Data
const mockMembers = [{
  id: '1',
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  tier: 'Sommelier',
  points: 2450,
  lifetimePoints: 5800,
  lastActivity: '2026-05-26T12:00:00Z',
  status: 'Active',
  tier_locked: true
}, {
  id: '2',
  name: 'James Chen',
  email: 'james.c@example.com',
  tier: 'Connoisseur',
  points: 850,
  lifetimePoints: 1200,
  lastActivity: '2026-05-26T09:00:00Z',
  status: 'Active',
  tier_locked: false
}, {
  id: '3',
  name: 'Emma Wilson',
  email: 'emma.w@example.com',
  tier: 'Enthusiast',
  points: 120,
  lifetimePoints: 350,
  lastActivity: '2026-05-25T14:00:00Z',
  status: 'Active',
  tier_locked: false
}, {
  id: '4',
  name: 'David Park',
  email: 'david.p@example.com',
  tier: 'Vintner',
  points: 15600,
  lifetimePoints: 42000,
  lastActivity: '2026-05-23T14:00:00Z',
  status: 'Active',
  tier_locked: true
}, {
  id: '5',
  name: 'Lisa Wang',
  email: 'lisa.w@example.com',
  tier: 'Connoisseur',
  points: 600,
  lifetimePoints: 950,
  lastActivity: '2026-05-19T14:00:00Z',
  status: 'Paused',
  tier_locked: false
}, {
  id: '6',
  name: 'Tom Harris',
  email: 'tom.h@example.com',
  tier: 'Enthusiast',
  points: 50,
  lifetimePoints: 150,
  lastActivity: '2026-05-12T14:00:00Z',
  status: 'Active',
  tier_locked: false
}, {
  id: '7',
  name: 'Sophie Miller',
  email: 'sophie.m@example.com',
  tier: 'Sommelier',
  points: 3100,
  lifetimePoints: 6200,
  lastActivity: '2026-04-26T14:00:00Z',
  status: 'Active',
  tier_locked: false
}, {
  id: '8',
  name: 'Michael Brown',
  email: 'michael.b@example.com',
  tier: 'Enthusiast',
  points: 0,
  lifetimePoints: 50,
  lastActivity: '2026-02-26T14:00:00Z',
  status: 'Expired',
  tier_locked: false
}];
export function LoyaltyMembersPage() {
  const navigate = useNavigate();
  const {
    addAuditEvent
  } = useLoyaltyAudit();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [allMatchingSelected, setAllMatchingSelected] = useState(false);
  const TOTAL_MATCHING_COUNT = 1247;
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  const [pointsMin, setPointsMin] = useState<string>('');
  const [pointsMax, setPointsMax] = useState<string>('');
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportColumns, setExportColumns] = useState<Set<string>>(new Set(['name', 'email', 'tier', 'points', 'lifetimePoints', 'lastActivity', 'status']));
  const [exportEmail, setExportEmail] = useState('staff@winery.example');
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'remove' | 'reset'>('add');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentReasonPreset, setAdjustmentReasonPreset] = useState('');
  const [adjustmentReasonCustom, setAdjustmentReasonCustom] = useState('');
  const [showTierPreview, setShowTierPreview] = useState(false);
  const [isReasonDropdownOpen, setIsReasonDropdownOpen] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [largeScaleCountConfirmation, setLargeScaleCountConfirmation] = useState('');
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  // Sort state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  // Bulk Change Tier state
  const [isTierModalOpen, setIsTierModalOpen] = useState(false);
  const [selectedTargetTier, setSelectedTargetTier] = useState('');
  const [lockTier, setLockTier] = useState(false);
  const [tierReasonPreset, setTierReasonPreset] = useState('');
  const [tierReasonCustom, setTierReasonCustom] = useState('');
  const [isTierReasonDropdownOpen, setIsTierReasonDropdownOpen] = useState(false);
  const [isTierDropdownOpen, setIsTierDropdownOpen] = useState(false);
  // Change Loyalty Status state
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<'pause' | 'resume'>('pause');
  const [statusReason, setStatusReason] = useState('');
  const [notifyMembers, setNotifyMembers] = useState(true);
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  // Emit a bulk loyalty audit event into the global Operations Activity Log.
  // Bulk actions aren't tied to a single member, so entity is '{n} members'
  // and entityType is 'bulk-members' (matches the pass-3 wiring spec).
  const logBulkAudit = (count: number, event: {
    eventType: string;
    activity: string;
    status?: string;
    changes?: {
      field: string;
      before: string;
      after: string;
    }[];
  }) => addAuditEvent({
    entity: `${count.toLocaleString()} members`,
    entityType: 'bulk-members',
    ...event
  });
  useEffect(() => {
    const hasActiveFilter = searchQuery !== '' || Object.values(filterValues).some(v => v && v.length > 0) || addedFilters.size > 0 || pointsMin !== '' || pointsMax !== '';
    const hasSelection = allMatchingSelected || selectedMembers.size > 0;
    if (hasActiveFilter && hasSelection) {
      setSelectedMembers(new Set());
      setAllMatchingSelected(false);
      setToasts(prev => [...prev, {
        id: `t-${Date.now()}`,
        type: 'info',
        title: 'Filter changed — selection cleared',
        duration: 4000,
        onClose: removeToast
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterValues, addedFilters, pointsMin, pointsMax]);
  // Auto-enable tier lock when the target tier would downgrade at least one member.
  // Only flips ON — does not auto-flip OFF, so user remains in control of the lock state.
  useEffect(() => {
    if (!selectedTargetTier) return;
    const targetMembers = mockMembers.filter(m => selectedMembers.has(m.id));
    const targetRank = getTierRank(selectedTargetTier);
    const hasDowngrade = targetMembers.some(m => getTierRank(m.tier) > targetRank);
    if (hasDowngrade) {
      setLockTier(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTargetTier, selectedMembers]);
  const handleOpenBulkModal = () => {
    setAdjustmentType('add');
    setAdjustmentAmount('');
    setAdjustmentReason('');
    setAdjustmentReasonPreset('');
    setAdjustmentReasonCustom('');
    setShowTierPreview(false);
    setIsReasonDropdownOpen(false);
    setResetConfirmation('');
    setLargeScaleCountConfirmation('');
    setIsBulkModalOpen(true);
  };
  const handleOpenTierModal = () => {
    setSelectedTargetTier('');
    setLockTier(false);
    setTierReasonPreset('');
    setTierReasonCustom('');
    setIsTierReasonDropdownOpen(false);
    setIsTierDropdownOpen(false);
    setIsTierModalOpen(true);
  };
  const handleOpenStatusModal = () => {
    setStatusAction('pause');
    setStatusReason('');
    setNotifyMembers(true);
    setIsStatusModalOpen(true);
  };
  const getTierRank = (tierName: string): number => {
    const index = TIER_THRESHOLDS.findIndex(t => t.name === tierName);
    return index >= 0 ? index : 0;
  };
  const availableFilters: Filter[] = [{
    id: 'tier',
    label: 'Tier',
    options: [{
      id: '1',
      label: 'Enthusiast',
      value: 'Enthusiast'
    }, {
      id: '2',
      label: 'Connoisseur',
      value: 'Connoisseur'
    }, {
      id: '3',
      label: 'Sommelier',
      value: 'Sommelier'
    }, {
      id: '4',
      label: 'Vintner',
      value: 'Vintner'
    }],
    selectedValues: filterValues.tier || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('tier')
  }, {
    id: 'status',
    label: 'Status',
    options: [{
      id: '1',
      label: 'Active',
      value: 'Active'
    }, {
      id: '2',
      label: 'Paused',
      value: 'Paused'
    }, {
      id: '3',
      label: 'Expired',
      value: 'Expired'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }];
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues(prev => ({
      ...prev,
      [filterId]: Array.isArray(value) ? value : [value]
    }));
  };
  const handleFilterRemove = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.delete(filterId);
    setAddedFilters(newAddedFilters);
    setFilterValues(prev => ({
      ...prev,
      [filterId]: []
    }));
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpen = new Set(openFilterDropdowns);
    if (newOpen.has(filterId)) {
      newOpen.delete(filterId);
    } else {
      newOpen.add(filterId);
    }
    setOpenFilterDropdowns(newOpen);
  };
  const handleAddFilter = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.add(filterId);
    setAddedFilters(newAdded);
  };
  const handleClearAll = () => {
    setFilterValues({});
    setAddedFilters(new Set());
    setSearchQuery('');
    setPointsMin('');
    setPointsMax('');
  };
  // Sort handler
  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (!current || current.key !== columnKey) {
        return {
          key: columnKey,
          direction: 'asc'
        };
      }
      if (current.direction === 'asc') {
        return {
          key: columnKey,
          direction: 'desc'
        };
      }
      return null;
    });
  };
  // Filtered and sorted members
  const filteredAndSortedMembers = useMemo(() => {
    let filtered = mockMembers.filter(member => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!member.name.toLowerCase().includes(query) && !member.email.toLowerCase().includes(query)) {
          return false;
        }
      }
      // Tier filter
      if (filterValues.tier && filterValues.tier.length > 0) {
        if (!filterValues.tier.includes(member.tier)) return false;
      }
      // Status filter
      if (filterValues.status && filterValues.status.length > 0) {
        if (!filterValues.status.includes(member.status)) return false;
      }
      // Points min/max range filter
      const minVal = pointsMin === '' ? null : parseInt(pointsMin);
      const maxVal = pointsMax === '' ? null : parseInt(pointsMax);
      if (minVal !== null && !isNaN(minVal) && member.points < minVal) return false;
      if (maxVal !== null && !isNaN(maxVal) && member.points > maxVal) return false;
      return true;
    });
    // Apply sorting
    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        switch (sortConfig.key) {
          case 'member':
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case 'tier':
            const tierOrder: Record<string, number> = {
              Enthusiast: 0,
              Connoisseur: 1,
              Sommelier: 2,
              Vintner: 3
            };
            aValue = tierOrder[a.tier] ?? 0;
            bValue = tierOrder[b.tier] ?? 0;
            break;
          case 'points':
            aValue = a.points;
            bValue = b.points;
            break;
          case 'lifetimePoints':
            aValue = a.lifetimePoints;
            bValue = b.lifetimePoints;
            break;
          case 'lastActivity':
            // ISO timestamps sort chronologically when converted to epoch ms
            aValue = new Date(a.lastActivity).getTime();
            bValue = new Date(b.lastActivity).getTime();
            break;
          case 'status':
            aValue = a.status.toLowerCase();
            bValue = b.status.toLowerCase();
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [mockMembers, searchQuery, filterValues, sortConfig, pointsMin, pointsMax]);
  const toggleSelectAll = () => {
    setAllMatchingSelected(false);
    if (selectedMembers.size === filteredAndSortedMembers.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(filteredAndSortedMembers.map(m => m.id)));
    }
  };
  const toggleSelectMember = (id: string) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedMembers(newSelected);
  };
  const getTierBadgeVariant = (tier: string) => {
    switch (tier) {
      case 'Enthusiast':
        return 'success';
      // Green
      case 'Connoisseur':
        return 'info';
      // Blue
      case 'Sommelier':
        return 'default';
      // Purple/Default (using default as purple isn't explicit in Badge props, checking Badge.tsx... default is gray/slate. Wait, request said "purple for Sommelier". Badge.tsx has success, warning, error, info, default. I'll use 'default' for now or maybe 'info' again if needed, but let's stick to available variants. Actually, I can use custom classes if needed, but Badge component encapsulates styles. Let's use 'info' for Connoisseur (Blue), 'success' for Enthusiast (Green). For Sommelier (Purple) and Vintner (Gold), I might need to use 'warning' for Gold. Purple isn't standard. I'll use 'default' for Sommelier and 'warning' for Vintner.)
      case 'Vintner':
        return 'warning';
      // Gold/Amber
      default:
        return 'default';
    }
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Paused':
        return 'warning';
      case 'Expired':
        return 'error';
      default:
        return 'default';
    }
  };
  // Single-member edge case: exactly 1 selected and NOT in filter-scoped mode.
  // In this state, bulk modals are inappropriate — redirect to Contact Detail instead.
  const isSingleMemberSelection = !allMatchingSelected && selectedMembers.size === 1;
  const singleMemberId = isSingleMemberSelection ? Array.from(selectedMembers)[0] : null;
  const singleMemberTooltip = "Open this member's loyalty details to adjust a single member";
  // Display count and scope for bulk modal titles/intro copy.
  const bulkScopeCount = allMatchingSelected ? TOTAL_MATCHING_COUNT : selectedMembers.size;
  const bulkScopeLabel = allMatchingSelected ? `all ${TOTAL_MATCHING_COUNT.toLocaleString()} matching members` : `${selectedMembers.size} ${selectedMembers.size === 1 ? 'member' : 'members'}`;
  // Soft-block: require typing the member count when scope is large + destructive.
  const isLargeScaleDestructive = allMatchingSelected && (adjustmentType === 'reset' || adjustmentType === 'remove' && adjustmentAmount !== '' && parseInt(adjustmentAmount) > 0);
  const largeScaleConfirmed = !isLargeScaleDestructive || largeScaleCountConfirmation === TOTAL_MATCHING_COUNT.toString();
  // Tier downgrade detection — used to nudge Lock tier ON and badge it as Recommended.
  const hasDowngradeInSelection = (() => {
    if (allMatchingSelected || !selectedTargetTier) return false;
    const targetMembers = mockMembers.filter(m => selectedMembers.has(m.id));
    const targetRank = getTierRank(selectedTargetTier);
    return targetMembers.some(m => getTierRank(m.tier) > targetRank);
  })();
  // Export scope — selection takes precedence over filter; mock returns TOTAL_MATCHING_COUNT when filters are active.
  const exportScopeCount = (() => {
    if (allMatchingSelected) return TOTAL_MATCHING_COUNT;
    if (selectedMembers.size > 0) return selectedMembers.size;
    return TOTAL_MATCHING_COUNT;
  })();
  const exportScopeDescriptor = (() => {
    if (allMatchingSelected) return `all ${TOTAL_MATCHING_COUNT.toLocaleString()} matching members`;
    if (selectedMembers.size > 0) return `${selectedMembers.size} selected ${selectedMembers.size === 1 ? 'member' : 'members'}`;
    return `all ${TOTAL_MATCHING_COUNT.toLocaleString()} members matching the current filter`;
  })();
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-9173" data-mp="38bpw3bltbbqp">
      {/* NavBar with breadcrumb-style title */}
      <div className="mb-6" data-id="element-9174" data-mp="sk1zjhapnl8f">
        <NavBar tabs={[]} title="Loyalty & Rewards › Members" data-id="element-9175" data-mp="snhfsng4f0bf" />
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-ocean-border-subtle rounded-lg p-4 mb-6 shadow-sm" data-id="element-9176" data-mp="noe9z0sbgq2e">
        <div className="flex flex-col gap-4" data-id="element-9177" data-mp="1mcm76nz6dzto">
          <div className="flex items-center gap-3" data-id="element-9178" data-mp="1hc6uzzqmqyda">
            <div className="flex-1 max-w-md" data-id="element-9179" data-mp="1hdi0q53myg71">
              <SearchBar placeholder="Search by name or email..." value={searchQuery} onChange={setSearchQuery} data-id="element-9180" data-mp="1vbg9muz6tkf5" />
            </div>
            <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-9181" data-mp="1qajybwciavzp" />
            <Button variant="outline" size="md" iconLeft={<Download className="w-4 h-4" data-id="element-9183" data-mp="2e0wchoychoyq" />} onClick={() => setIsExportModalOpen(true)} data-id="element-9182" data-mp="2e27ebj88cmn7">
              Export
            </Button>
          </div>
          {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-9184" data-mp="2o0wy5hjosh63">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-9185" data-mp="2j0sh9qev9kfc" />
              <Button variant="ghost" size="sm" onClick={handleClearAll} data-id="element-9186" data-mp="37kc91test1rk">
                Clear all
              </Button>
            </div>}
          {/* Points range filter */}
          <div className="flex items-center gap-3 pt-2 border-t border-[#F1F5F9]" data-id="element-9187" data-mp="2xmofv7wuc2bi">
            <span className="text-[12px] font-medium text-[#475569]" data-id="element-9188" data-mp="2jvjcpvu9krwf">
              Points range:
            </span>
            <div className="flex items-center gap-2" data-id="element-9189" data-mp="2on1h6tvhxam5">
              <input type="number" value={pointsMin} onChange={e => setPointsMin(e.target.value)} placeholder="Min" className="w-[100px] h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-9190" data-mp="2oomakg62qnif" />
              <span className="text-[13px] text-[#64748B]" data-id="element-9191" data-mp="24yai8t9nz80h">to</span>
              <input type="number" value={pointsMax} onChange={e => setPointsMax(e.target.value)} placeholder="Max" className="w-[100px] h-[36px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[13px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-9192" data-mp="bgcwibjpe645" />
              {(pointsMin !== '' || pointsMax !== '') && <button onClick={() => {
              setPointsMin('');
              setPointsMax('');
            }} className="text-[12px] text-[#0EA5E9] hover:underline font-medium ml-1" data-id="element-9193" data-mp="udw7vltmw0tq">
                  Clear
                </button>}
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white border border-ocean-border-subtle rounded-lg shadow-sm overflow-hidden" data-id="element-9194" data-mp="gemqwjxlt2op">
        {selectedMembers.size > 0 && <div className="bg-white border-b border-[#E2E8F0]" data-id="element-9195" data-mp="315evh1jw8kvq">
            {selectedMembers.size === filteredAndSortedMembers.length && !allMatchingSelected && selectedMembers.size < TOTAL_MATCHING_COUNT && <div className="bg-[#F0F9FF] border-b border-[#E2E8F0] px-6 py-2.5 flex items-center justify-center gap-2 text-[13px]" data-id="element-9196" data-mp="1gljijljwrfx8">
                  <span className="text-[#475569]" data-id="element-9197" data-mp="1pnspy2jvqt6e">
                    All {selectedMembers.size} on this page are selected.
                  </span>
                  <button className="text-[#0EA5E9] font-medium hover:underline" onClick={() => setAllMatchingSelected(true)} data-id="element-9198" data-mp="1uguaor6rpmxw">
                    Select all {TOTAL_MATCHING_COUNT.toLocaleString()} matching
                    members
                  </button>
                </div>}
            <div className="px-6 py-4 flex items-center gap-4" data-id="element-9199" data-mp="28f25asgc0c5w">
              <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-9200" data-mp="2ddviskvmc362">
                {allMatchingSelected ? `All ${TOTAL_MATCHING_COUNT.toLocaleString()} matching members selected` : `${selectedMembers.size} selected`}
              </span>
              <button className="text-[13px] text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer" onClick={() => {
            setSelectedMembers(new Set());
            setAllMatchingSelected(false);
          }} data-id="element-9201" data-mp="j1otki5w3lur">
                Clear selection
              </button>
              <div className="flex-1" data-id="element-9202" data-mp="3tshkpv5yu32z" />
              {isSingleMemberSelection ? <span title={singleMemberTooltip} className="inline-flex" data-id="element-9203" data-mp="3otn1wt800eiv">
                  <Button variant="outline" size="md" onClick={() => navigate(`/contacts/${singleMemberId}?tab=loyalty`)} iconLeft={<Edit className="w-4 h-4" data-id="element-9205" data-mp="2w5r57oifimm3" />} data-id="element-9204" data-mp="3142gm3odh56f">
                    Bulk Adjust
                  </Button>
                </span> : <Button variant="outline" size="md" onClick={handleOpenBulkModal} iconLeft={<Edit className="w-4 h-4" data-id="element-9207" data-mp="362vjl2619dsc" />} data-id="element-9206" data-mp="3av6u0yyeghz5">
                  Bulk Adjust
                </Button>}
              {isSingleMemberSelection ? <span title={singleMemberTooltip} className="inline-flex" data-id="element-9208" data-mp="2icswz1kw1014">
                  <Button variant="outline" size="md" onClick={() => navigate(`/contacts/${singleMemberId}?tab=loyalty`)} iconLeft={<Shield className="w-4 h-4" data-id="element-9210" data-mp="svnve85c7nfu" />} data-id="element-9209" data-mp="2df9hxzgd8j84">
                    Change Tier
                  </Button>
                </span> : <Button variant="outline" size="md" onClick={handleOpenTierModal} iconLeft={<Shield className="w-4 h-4" data-id="element-9212" data-mp="izaryijzpv7w" />} data-id="element-9211" data-mp="xo75uw8qa2gf">
                  Change Tier
                </Button>}
              {isSingleMemberSelection ? <span title={singleMemberTooltip} className="inline-flex" data-id="element-9213" data-mp="ny5he3cgqi0z">
                  <Button variant="outline" size="md" onClick={() => navigate(`/contacts/${singleMemberId}?tab=loyalty`)} iconLeft={<ToggleLeft className="w-4 h-4" data-id="element-9215" data-mp="1gkyqpa3x4pml" />} data-id="element-9214" data-mp="1bo88oh8jzjyj">
                    Change Status
                  </Button>
                </span> : <Button variant="outline" size="md" onClick={handleOpenStatusModal} iconLeft={<ToggleLeft className="w-4 h-4" data-id="element-9217" data-mp="16mivow1rsw7t" />} data-id="element-9216" data-mp="12ol6vubwwc4y">
                  Change Status
                </Button>}
            </div>
          </div>}
        <div className="overflow-x-auto" data-id="element-9218" data-mp="3recn0fefupce">
          <table className="w-full" data-id="element-9219" data-mp="3rfdxahnmr7t2">
            <thead className="bg-[#F1F5F9]" data-id="element-9220" data-mp="3rc8axa6dyk11">
              <tr data-id="element-9221" data-mp="1xvvjcgytuz21">
                <th className="w-12 px-4 py-3 border-b border-[#E2E8F0]" data-id="element-9222" data-mp="1xuaosrcijg46">
                  <div className="flex items-center justify-center" data-id="element-9223" data-mp="1svzhan9oxhp4">
                    <input type="checkbox" className={`w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] ${allMatchingSelected ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`} checked={allMatchingSelected || selectedMembers.size === filteredAndSortedMembers.length && filteredAndSortedMembers.length > 0} disabled={allMatchingSelected} onChange={toggleSelectAll} data-id="element-9224" data-mp="1syvf8bljg6dk" />
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-9225" data-mp="157znfeh31b6b">
                  <button onClick={() => handleSort('member')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-9226" data-mp="109enzh9nmvix">
                    Member
                    {sortConfig?.key === 'member' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9227" data-mp="2uk1vrq1zqvqv" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9228" data-mp="331u4hbru8oq5" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-9229" data-mp="374gz1kfw8nr1" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-9230" data-mp="3buoz7bjoj21o">
                  <button onClick={() => handleSort('tier')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-9231" data-mp="3gt8kraud2bti">
                    Tier
                    {sortConfig?.key === 'tier' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9232" data-mp="2o64ktka4knol" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9233" data-mp="2t466mcyxr13r" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-9234" data-mp="2t3ea8zxikzcf" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-9235" data-mp="2y1fuvpal96q0">
                  <button onClick={() => handleSort('points')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors justify-end" data-id="element-9236" data-mp="ddb9szodkzu4">
                    Points
                    {sortConfig?.key === 'points' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9237" data-mp="uhletpomn79t" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9238" data-mp="1875cwi79jgcs" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-9239" data-mp="18900k49x5mkk" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-9240" data-mp="13evpb8tmesdo">
                  <button onClick={() => handleSort('lifetimePoints')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors justify-end" data-id="element-9241" data-mp="1d6i6rmwjxnov">
                    Lifetime Points
                    {sortConfig?.key === 'lifetimePoints' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9242" data-mp="1m7zp7bxfm1gq" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9243" data-mp="3swupspoeos2u" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-9244" data-mp="3szqpaifwjote" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-9245" data-mp="23giruocipovh">
                  <button onClick={() => handleSort('lastActivity')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-9246" data-mp="28g50sllbciyh">
                    Last Activity
                    {sortConfig?.key === 'lastActivity' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9247" data-mp="1ynz819n8lxi8" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9248" data-mp="1kprivnwg00hm" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-9249" data-mp="1ks3z9wmjy2sk" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-9250" data-mp="1bpcmnthxx0zc">
                  <button onClick={() => handleSort('status')} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-9251" data-mp="1fqemabfrrf1u">
                    Status
                    {sortConfig?.key === 'status' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9252" data-mp="3a3nve24ekyjk" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-9253" data-mp="goyynaeu1vse" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-9254" data-mp="gqa37cg3r7kk" />}
                  </button>
                </th>
                <th className="w-12 px-4 py-3 border-b border-[#E2E8F0]" aria-hidden="true" data-id="element-9255" data-mp="llpatmnio78j"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]" data-id="element-9256" data-mp="ugnadcl66i8u">
              {filteredAndSortedMembers.map(member => {
              const isSelected = selectedMembers.has(member.id);
              return <tr key={member.id} className={`transition-colors cursor-pointer ${isSelected ? 'bg-[#F0F9FF]' : 'hover:bg-[#F8FAFC]'}`} onClick={() => navigate(`/contacts/${member.id}?tab=loyalty`)} data-id="element-9257" data-mp="14e1eccpwpek9">
                    <td className="px-4 py-4" onClick={e => e.stopPropagation()} data-id="element-9258" data-mp="1deprz6be1cwm">
                      <div className="flex items-center justify-center" data-id="element-9259" data-mp="3b4r2zdng8deo">
                        <input type="checkbox" className={`w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] ${allMatchingSelected ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`} checked={allMatchingSelected || isSelected} disabled={allMatchingSelected} onChange={() => toggleSelectMember(member.id)} data-id="element-9260" data-mp="3b6k9msy2axmo" />
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-9261" data-mp="2xxjrib6vxja8">
                      <div data-id="element-9262" data-mp="2tvov9l3t9w3b">
                        <div className="text-sm font-medium text-[#0F172A] hover:text-[#0EA5E9] transition-colors" data-id="element-9263" data-mp="2twgdzvcb7zc6">
                          {member.name}
                        </div>
                        <div className="text-[13px] text-[#94A3B8] mt-1" data-id="element-9264" data-mp="3lmcswn6a0jc8">
                          {member.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-9265" data-mp="3cmpr409mxbin">
                      <div className="flex items-center gap-1.5" data-id="element-9266" data-mp="3clmxvlouawzk">
                        <Badge variant={getTierBadgeVariant(member.tier)} data-id="element-9267" data-mp="266vrx3kv0lyo">
                          {member.tier}
                        </Badge>
                        {member.tier_locked && <span title="Tier locked by staff — won't auto-evaluate" className="inline-flex" data-id="element-9268" data-mp="22o6kcr7vgf23">
                            <Lock className="w-3.5 h-3.5 text-[#94A3B8]" aria-label="Tier locked by staff" data-id="element-9269" data-mp="27njc9qdlocbj" />
                          </span>}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-[#0F172A]" data-id="element-9270" data-mp="1xoaoxgd8pvy5">
                      {member.points.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-right text-sm text-[#94A3B8]" data-id="element-9271" data-mp="1jvm203pstscj">
                      {member.lifetimePoints.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-sm text-[#475569]" data-id="element-9272" data-mp="1a0u7x9vfdnmi">
                      {formatRelativeTime(member.lastActivity)}
                    </td>
                    <td className="px-4 py-4" data-id="element-9273" data-mp="10y1f326lh3uk">
                      <Badge variant={getStatusBadgeVariant(member.status)} data-id="element-9274" data-mp="10zcs7slxjnyc">
                        {member.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 text-right" data-id="element-9275" data-mp="mcx8ymmx83kg">
                      <ChevronRight className="w-4 h-4 text-[#94A3B8] inline-block" aria-label={`Open ${member.name} loyalty details`} data-id="element-9276" data-mp="hcrgj48tud1b" />
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-ocean-border-subtle bg-white" data-id="element-9277" data-mp="r9nsjjxxpham">
          <Pagination currentPage={currentPage} totalPages={50} onPageChange={setCurrentPage} itemsPerPage={25} totalItems={1247} data-id="element-9278" data-mp="1595iw3qvp2m3" />
        </div>
      </div>

      {/* Bulk Adjust Points Modal */}
      {isBulkModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsBulkModalOpen(false)} data-id="element-9279" data-mp="1f2cmpk5z0ak2" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-9280" data-mp="1a3szlgfn4sq4">
            <div className="bg-white rounded-lg shadow-xl w-[420px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-9281" data-mp="1nz542fjllxk8">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-9282" data-mp="3dhtuhnyvetiw">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-9283" data-mp="3iflnklb94loo">
                  Adjust points for {bulkScopeLabel}
                </h2>
                <IconButton icon={<X className="w-5 h-5" data-id="element-9285" data-mp="3ra004g4edi43" />} label="Close modal" variant="ghost" onClick={() => setIsBulkModalOpen(false)} data-id="element-9284" data-mp="3r9i6lr94dhzv" />
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 space-y-5 overflow-y-auto" data-id="element-9286" data-mp="2ziaop1tj7u9r">
                <p className="text-sm text-[#475569]" data-id="element-9287" data-mp="2zkoil7ch70fq">
                  This will apply to{' '}
                  <span className="font-semibold text-[#0F172A]" data-id="element-9288" data-mp="33ma23bjimcyw">
                    {bulkScopeLabel}
                  </span>
                  .
                </p>

                {/* Adjustment Type */}
                <div data-id="element-9289" data-mp="iwax9axrok0i">
                  <label className="block text-xs font-medium text-[#475569] mb-2" data-id="element-9290" data-mp="nsrmy9km76fp">
                    Adjustment Type
                  </label>
                  <div className="flex gap-4 flex-wrap" data-id="element-9291" data-mp="xwgg6hmh605d">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-9292" data-mp="sxd96rmxpjur">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${adjustmentType === 'add' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => {
                    setAdjustmentType('add');
                    setAdjustmentReasonPreset('');
                    setAdjustmentReasonCustom('');
                    setIsReasonDropdownOpen(false);
                    setResetConfirmation('');
                    setLargeScaleCountConfirmation('');
                  }} data-id="element-9293" data-mp="o75a73uvxblp">
                        {adjustmentType === 'add' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-9294" data-mp="17qwqmv78zha" />}
                      </div>
                      <span className="text-sm text-[#0F172A]" data-id="element-9295" data-mp="1zl0oybhvnice">Add Points</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-9296" data-mp="1vl8c3mlgaq18">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${adjustmentType === 'remove' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => {
                    setAdjustmentType('remove');
                    setAdjustmentReasonPreset('');
                    setAdjustmentReasonCustom('');
                    setIsReasonDropdownOpen(false);
                    setResetConfirmation('');
                    setLargeScaleCountConfirmation('');
                  }} data-id="element-9297" data-mp="3p2edjpn9lgml">
                        {adjustmentType === 'remove' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-9298" data-mp="gl9rb5fxgnbs" />}
                      </div>
                      <span className="text-sm text-[#0F172A]" data-id="element-9299" data-mp="7mxtaicqen9b">
                        Remove Points
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-9300" data-mp="2ndahs0ncujuo">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${adjustmentType === 'reset' ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} onClick={() => {
                    setAdjustmentType('reset');
                    setAdjustmentReasonPreset('');
                    setAdjustmentReasonCustom('');
                    setIsReasonDropdownOpen(false);
                    setResetConfirmation('');
                    setLargeScaleCountConfirmation('');
                  }} data-id="element-9301" data-mp="2se81wirxdnyt">
                        {adjustmentType === 'reset' && <div className="w-2 h-2 rounded-full bg-[#EF4444]" data-id="element-9302" data-mp="213bxl65av82a" />}
                      </div>
                      <span className="text-sm text-[#0F172A]" data-id="element-9303" data-mp="1w4qwz5m4ymdb">
                        Reset to Zero
                      </span>
                    </label>
                  </div>
                </div>

                {/* Reset to Zero Warning & Confirmation */}
                {adjustmentType === 'reset' && (() => {
              const targetMembers = selectedMembers.size > 0 ? mockMembers.filter(m => selectedMembers.has(m.id)) : mockMembers;
              const memberCount = allMatchingSelected ? TOTAL_MATCHING_COUNT : targetMembers.length;
              return <div className="space-y-4" data-id="element-9304" data-mp="wwn9ndvht54g">
                        {/* Destructive warning */}
                        <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-9305" data-mp="11u5ofkw6yekd">
                          <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" data-id="element-9306" data-mp="16ryri5276sgf" />
                          <p className="text-[13px] text-[#991B1B]" data-id="element-9307" data-mp="1atkbsbh2a90n">
                            This will set the current points balance to 0 for{' '}
                            <span className="font-semibold" data-id="element-9308" data-mp="1kl6jvyy3rrz6">{memberCount}</span>{' '}
                            member{memberCount !== 1 ? 's' : ''}. Lifetime
                            points will not be affected. This action cannot be
                            undone.
                          </p>
                        </div>

                        {/* Confirmation input */}
                        <div data-id="element-9309" data-mp="3j3rsqr968qkg">
                          <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9310" data-mp="3j41lunpijdud">
                            Confirm Reset
                          </label>
                          <input type="text" value={resetConfirmation} onChange={e => setResetConfirmation(e.target.value)} placeholder="Type RESET to confirm" className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-9311" data-mp="3a225lxmbodsh" />
                          <p className="text-[12px] text-[#64748B] mt-1" data-id="element-9312" data-mp="3t0o3l3mjlos6">
                            Type RESET to confirm
                          </p>
                        </div>
                      </div>;
            })()}

                {/* Amount - hidden for reset */}
                {adjustmentType !== 'reset' && <Input type="number" label="Amount" value={adjustmentAmount} onChange={e => setAdjustmentAmount(e.target.value)} placeholder="0" data-id="element-9313" data-mp="kkl7lv4slojo" />}

                {/* Reason Dropdown */}
                <div data-id="element-9314" data-mp="2cgt9ibzwq903">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9315" data-mp="2cie4g2q4vtyh">
                    Reason <span className="text-[#EF4444]" data-id="element-9316" data-mp="27p1sot6jzhn5">*</span>
                  </label>
                  <div className="relative" data-id="element-9317" data-mp="22q8fl1v6hsni">
                    <button type="button" onClick={() => setIsReasonDropdownOpen(!isReasonDropdownOpen)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-left flex items-center justify-between focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-9318" data-mp="22p5mcnadve4f">
                      <span className={adjustmentReasonPreset ? 'text-[#0F172A]' : 'text-[#94A3B8]'} data-id="element-9319" data-mp="1jtfjyyyjgfq0">
                        {adjustmentReasonPreset || 'Select a reason...'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-[#475569] transition-transform ${isReasonDropdownOpen ? 'rotate-180' : ''}`} data-id="element-9320" data-mp="3e7gghd6m8qmx" />
                    </button>
                    {isReasonDropdownOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-[6px] shadow-lg overflow-hidden" data-id="element-9321" data-mp="3qnzfam2ih0zt">
                        {(adjustmentType === 'add' ? ADD_REASON_PRESETS : REMOVE_REASON_PRESETS).map(reason => <button key={reason} type="button" onClick={() => {
                    setAdjustmentReasonPreset(reason);
                    setIsReasonDropdownOpen(false);
                    if (reason !== 'Other') {
                      setAdjustmentReasonCustom('');
                    }
                  }} className={`w-full px-3 py-2.5 text-[14px] text-left hover:bg-[#F1F5F9] transition-colors ${adjustmentReasonPreset === reason ? 'bg-[#F0F9FF] text-[#0EA5E9] font-medium' : 'text-[#0F172A]'}`} data-id="element-9322" data-mp="48og89lh98hu">
                            {reason}
                          </button>)}
                      </div>}
                  </div>
                  {adjustmentReasonPreset === 'Other' && <input type="text" value={adjustmentReasonCustom} onChange={e => setAdjustmentReasonCustom(e.target.value)} placeholder="Enter custom reason..." className="w-full h-[40px] px-3 mt-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-9323" data-mp="2szysebzn5drp" />}
                </div>

                {/* Soft-block: large-scale destructive confirmation */}
                {isLargeScaleDestructive && <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-[6px] px-4 py-3 space-y-3" data-id="element-9324" data-mp="259uoe3ijjc06">
                    <div className="flex items-start gap-2.5" data-id="element-9325" data-mp="2ecdq2kalbiku">
                      <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" data-id="element-9326" data-mp="2easx2z3xc7p3" />
                      <div className="flex-1" data-id="element-9327" data-mp="1qlgmbusy7rko">
                        <p className="text-[13px] font-semibold text-[#991B1B] mb-1" data-id="element-9328" data-mp="1ml667pbfzks3">
                          Large-scale destructive action
                        </p>
                        <p className="text-[13px] text-[#991B1B]" data-id="element-9329" data-mp="1cfy8sp17a6h0">
                          This will affect{' '}
                          <span className="font-semibold" data-id="element-9330" data-mp="1h9sqjmx8wbdg">
                            {TOTAL_MATCHING_COUNT.toLocaleString()}
                          </span>{' '}
                          members across the current filter. Type the member
                          count below to confirm you understand the scale.
                        </p>
                      </div>
                    </div>
                    <div data-id="element-9331" data-mp="204hh34k92j93">
                      <label className="block text-[12px] font-medium text-[#991B1B] mb-1.5" data-id="element-9332" data-mp="1l9tb7paxtisy">
                        Type the member count to confirm
                      </label>
                      <input type="text" value={largeScaleCountConfirmation} onChange={e => setLargeScaleCountConfirmation(e.target.value)} placeholder={TOTAL_MATCHING_COUNT.toString()} className="w-full h-[40px] px-3 bg-white border border-[#FCA5A5] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#CBD5E1] focus:outline-none focus:border-[#DC2626] focus:ring-[3px] focus:ring-[#DC2626]/20 transition-all duration-200" data-id="element-9333" data-mp="9ue6u0gefont" />
                    </div>
                  </div>}

                {/* Preview Summary & Warnings (add/remove only) */}
                {adjustmentAmount && parseInt(adjustmentAmount) > 0 && (() => {
              const amount = parseInt(adjustmentAmount);
              // FILTER-SCOPED MODE: aggregate-only preview, no per-member calculations
              if (allMatchingSelected) {
                return <div className="space-y-3" data-id="element-9334" data-mp="140sfcx179dgu">
                          {/* Summary */}
                          <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-9335" data-mp="z2qmxlhvw1sp">
                            <p className="text-[13px] font-medium text-[#0F172A]" data-id="element-9336" data-mp="3orj8dne7u1vh">
                              This will{' '}
                              {adjustmentType === 'add' ? 'add' : 'remove'}{' '}
                              <span className="font-semibold" data-id="element-9337" data-mp="1zap23r2e4tzu">
                                {amount.toLocaleString()}
                              </span>{' '}
                              points
                              {adjustmentType === 'add' ? ' to ' : ' from '}
                              <span className="font-semibold" data-id="element-9338" data-mp="296s1wq9rsgjm">
                                {TOTAL_MATCHING_COUNT.toLocaleString()}
                              </span>{' '}
                              members matching the current filter
                            </p>
                          </div>

                          {/* Filter-scoped aggregate notice — replaces per-member tier preview */}
                          <div className="bg-[#DBEAFE] border border-[#BFDBFE] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-9339" data-mp="2wy5u8w7x8c4l">
                            <Info className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" data-id="element-9340" data-mp="xlv1e42duukh" />
                            <p className="text-[13px] text-[#1E40AF]" data-id="element-9341" data-mp="12jmrcsjgw6m1">
                              Some members may move to a new tier as a result.
                              Per-member preview isn't available when applying
                              to all matching members — review individual
                              changes from the Activity Log after the action
                              completes.
                            </p>
                          </div>

                          {adjustmentType === 'remove' && <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-9342" data-mp="1dhjdqddfpy1g">
                              <AlertTriangle className="w-4 h-4 text-[#D97706] mt-0.5 shrink-0" data-id="element-9343" data-mp="19gh9tj8koxt5" />
                              <p className="text-[13px] text-[#92400E]" data-id="element-9344" data-mp="1n7cm75ia5x1h">
                                Members with a balance below{' '}
                                {amount.toLocaleString()} points will be set to
                                0 (insufficient balance). They cannot go
                                negative.
                              </p>
                            </div>}
                        </div>;
              }
              const targetMembers = selectedMembers.size > 0 ? mockMembers.filter(m => selectedMembers.has(m.id)) : mockMembers;
              const memberCount = allMatchingSelected ? TOTAL_MATCHING_COUNT : targetMembers.length;
              // Calculate members going below zero
              const membersGoingBelowZero = adjustmentType === 'remove' ? targetMembers.filter(m => m.points < amount) : [];
              // Calculate tier changes (exclude members going below zero since they already have a warning)
              const membersGoingBelowZeroIds = new Set(membersGoingBelowZero.map(m => m.id));
              const tierChanges = targetMembers.filter(m => !membersGoingBelowZeroIds.has(m.id)).map(m => {
                const newPoints = adjustmentType === 'add' ? m.points + amount : Math.max(0, m.points - amount);
                const currentTier = getTierForPoints(m.points);
                const newTier = getTierForPoints(newPoints);
                if (currentTier !== newTier) {
                  return {
                    name: m.name,
                    currentTier,
                    newTier
                  };
                }
                return null;
              }).filter(Boolean) as {
                name: string;
                currentTier: string;
                newTier: string;
              }[];
              return <div className="space-y-3" data-id="element-9345" data-mp="1apyv93pr2als">
                        {/* Summary */}
                        <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-9346" data-mp="15ww2uywpl776">
                          <p className="text-[13px] font-medium text-[#0F172A]" data-id="element-9347" data-mp="11vaplcg9iqwg">
                            This will{' '}
                            {adjustmentType === 'add' ? 'add' : 'remove'}{' '}
                            <span className="font-semibold" data-id="element-9348" data-mp="rwuz9bp27lo0">
                              {amount.toLocaleString()}
                            </span>{' '}
                            points
                            {adjustmentType === 'add' ? ' to ' : ' from '}
                            <span className="font-semibold" data-id="element-9349" data-mp="htppioshzoeh">
                              {memberCount}
                            </span>{' '}
                            member{memberCount !== 1 ? 's' : ''}
                          </p>
                        </div>

                        {/* Below zero warning */}
                        {membersGoingBelowZero.length > 0 && <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-9350" data-mp="3qbcbwgpmbg2h">
                            <AlertTriangle className="w-4 h-4 text-[#D97706] mt-0.5 shrink-0" data-id="element-9351" data-mp="42kxo8vdg0ep" />
                            <p className="text-[13px] text-[#92400E]" data-id="element-9352" data-mp="40qdwxvu6ec7">
                              <span className="font-semibold" data-id="element-9353" data-mp="8um5nilib4yx">
                                {membersGoingBelowZero.length}
                              </span>{' '}
                              member
                              {membersGoingBelowZero.length !== 1 ? 's' : ''}{' '}
                              will be set to 0 points (insufficient balance)
                            </p>
                          </div>}

                        {/* Tier change notice */}
                        {tierChanges.length > 0 && <div className="bg-[#DBEAFE] border border-[#BFDBFE] rounded-[6px] px-4 py-3" data-id="element-9354" data-mp="2atjbrlnagh2i">
                            <div className="flex items-start gap-2.5" data-id="element-9355" data-mp="2fuyu35y309h7">
                              <Info className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" data-id="element-9356" data-mp="220phagz9tb42" />
                              <div className="flex-1" data-id="element-9357" data-mp="26t929z0af8iw">
                                <p className="text-[13px] text-[#1E40AF]" data-id="element-9358" data-mp="1s3bcxfqmgcs4">
                                  <span className="font-semibold" data-id="element-9359" data-mp="1s16xq1n9vrcj">
                                    {tierChanges.length}
                                  </span>{' '}
                                  member{tierChanges.length !== 1 ? 's' : ''}{' '}
                                  will move to a new tier as a result
                                </p>
                                <button type="button" onClick={() => setShowTierPreview(!showTierPreview)} className="text-[12px] font-medium text-[#2563EB] hover:text-[#1D4ED8] mt-1 transition-colors" data-id="element-9360" data-mp="14gxwyqv5rhvj">
                                  {showTierPreview ? 'Hide changes' : 'Preview changes'}
                                </button>
                              </div>
                            </div>
                            {showTierPreview && <div className="mt-3 pt-3 border-t border-[#BFDBFE] space-y-2" data-id="element-9361" data-mp="1nd7iu0caxcq3">
                                {tierChanges.map((change, i) => <div key={i} className="flex items-center gap-2 text-[12px]" data-id="element-9362" data-mp="1iduxxl4zqfq5" data-mpi={i}>
                                    <span className="text-[#1E40AF] font-medium truncate max-w-[120px]" data-id="element-9363" data-mp="1w96uly2pig9p">
                                      {change.name}
                                    </span>
                                    <span className="text-[#60A5FA]" data-id="element-9364" data-mp="2635n05czalog">
                                      {change.currentTier}
                                    </span>
                                    <ArrowRight className="w-3 h-3 text-[#60A5FA] shrink-0" data-id="element-9365" data-mp="yuc1l4lvn9mg" />
                                    <span className="text-[#1E40AF] font-semibold" data-id="element-9366" data-mp="12v5rg3ivqyhz">
                                      {change.newTier}
                                    </span>
                                  </div>)}
                              </div>}
                          </div>}
                      </div>;
            })()}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-9367" data-mp="1mhk45erx84ks">
                <Button variant="ghost" size="md" onClick={() => setIsBulkModalOpen(false)} data-id="element-9368" data-mp="1ql03iivdkj16">
                  Cancel
                </Button>
                {adjustmentType === 'reset' ? <Button variant="danger" size="md" disabled={resetConfirmation !== 'RESET' || !largeScaleConfirmed} onClick={() => {
              const count = allMatchingSelected ? TOTAL_MATCHING_COUNT : selectedMembers.size > 0 ? selectedMembers.size : mockMembers.length;
              const reason = adjustmentReasonPreset === 'Other' ? adjustmentReasonCustom.trim() : adjustmentReasonPreset;
              // Live audit (pass 3): bulk points reset
              logBulkAudit(count, {
                eventType: 'Loyalty',
                activity: `Bulk points reset — ${count.toLocaleString()} members set to 0`,
                changes: [{
                  field: 'Points Balance',
                  before: '—',
                  after: '0'
                }, {
                  field: 'Reason',
                  before: '—',
                  after: reason || 'Manual reset'
                }]
              });
              setToasts(prev => [...prev, {
                id: `t-${Date.now()}`,
                type: 'success',
                title: `Reset points to zero for ${count.toLocaleString()} ${count === 1 ? 'member' : 'members'}`,
                duration: 5000,
                onClose: removeToast
              }]);
              setIsBulkModalOpen(false);
              setSelectedMembers(new Set());
              setAllMatchingSelected(false);
            }} data-id="element-9369" data-mp="3sbjxrvv9jh9t">
                    Reset Points
                  </Button> : <Button variant="primary" size="md" disabled={!largeScaleConfirmed} onClick={() => {
              const amount = parseInt(adjustmentAmount) || 0;
              const count = allMatchingSelected ? TOTAL_MATCHING_COUNT : selectedMembers.size > 0 ? selectedMembers.size : mockMembers.length;
              const verb = adjustmentType === 'add' ? 'Added' : 'Removed';
              const preposition = adjustmentType === 'add' ? 'to' : 'from';
              const reason = adjustmentReasonPreset === 'Other' ? adjustmentReasonCustom.trim() : adjustmentReasonPreset;
              // Live audit (pass 3): bulk points add/remove
              logBulkAudit(count, {
                eventType: 'Loyalty',
                activity: `Bulk points adjustment — ${adjustmentType === 'add' ? '+' : '-'}${amount.toLocaleString()} points ${preposition} ${count.toLocaleString()} members (${reason})`,
                changes: [{
                  field: 'Points',
                  before: '—',
                  after: `${adjustmentType === 'add' ? '+' : '-'}${amount.toLocaleString()}`
                }, {
                  field: 'Reason',
                  before: '—',
                  after: reason
                }]
              });
              setToasts(prev => [...prev, {
                id: `t-${Date.now()}`,
                type: 'success',
                title: `${verb} ${amount.toLocaleString()} points ${preposition} ${count.toLocaleString()} ${count === 1 ? 'member' : 'members'}`,
                duration: 5000,
                onClose: removeToast
              }]);
              setIsBulkModalOpen(false);
              setSelectedMembers(new Set());
              setAllMatchingSelected(false);
            }} data-id="element-9370" data-mp="158o83fh3s0j">
                    Apply Adjustment
                  </Button>}
              </div>
            </div>
          </div>
        </>}

      {/* Bulk Change Tier Modal */}
      {isTierModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsTierModalOpen(false)} data-id="element-9371" data-mp="2p7io0qdn2jik" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-9372" data-mp="2u2y1hh7qideg">
            <div className="bg-white rounded-lg shadow-xl w-[420px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-9373" data-mp="2z1spqyoh0u5y">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-9374" data-mp="26baqy9us5eyc">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-9375" data-mp="2b8srpcyn8nv9">
                  Change tier for {bulkScopeLabel}
                </h2>
                <IconButton icon={<X className="w-5 h-5" data-id="element-9377" data-mp="3mq26hm6turq8" />} label="Close modal" variant="ghost" onClick={() => setIsTierModalOpen(false)} data-id="element-9376" data-mp="3mpiz2zeszlqg" />
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 space-y-5 overflow-y-auto" data-id="element-9378" data-mp="1g0qru2sab9iz">
                <p className="text-sm text-[#475569]" data-id="element-9379" data-mp="1g336a5yu31ra">
                  This will apply to{' '}
                  <span className="font-semibold text-[#0F172A]" data-id="element-9380" data-mp="1b40cjhob0hym">
                    {bulkScopeLabel}
                  </span>
                  .
                </p>

                {/* Target Tier Dropdown */}
                <div data-id="element-9381" data-mp="11a9pmh7tsfl1">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9382" data-mp="xb21yo3aijah">
                    Target Tier <span className="text-[#EF4444]" data-id="element-9383" data-mp="sawgjpnm5t0u">*</span>
                  </label>
                  <div className="relative" data-id="element-9384" data-mp="nc31vtwlbvz3">
                    <button type="button" onClick={() => setIsTierDropdownOpen(!isTierDropdownOpen)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-left flex items-center justify-between focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-9385" data-mp="ncuz195u61rh">
                      <span className={selectedTargetTier ? 'text-[#0F172A]' : 'text-[#94A3B8]'} data-id="element-9386" data-mp="1lm6w6g3eb4fs">
                        {selectedTargetTier || 'Select a tier...'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-[#475569] transition-transform ${isTierDropdownOpen ? 'rotate-180' : ''}`} data-id="element-9387" data-mp="1ctlez4fex5t2" />
                    </button>
                    {isTierDropdownOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-[6px] shadow-lg overflow-hidden" data-id="element-9388" data-mp="1gsuaoeu0jdr9">
                        {TIER_THRESHOLDS.map(tier => <button key={tier.name} type="button" onClick={() => {
                    setSelectedTargetTier(tier.name);
                    setIsTierDropdownOpen(false);
                  }} className={`w-full px-3 py-2.5 text-[14px] text-left hover:bg-[#F1F5F9] transition-colors ${selectedTargetTier === tier.name ? 'bg-[#F0F9FF] text-[#0EA5E9] font-medium' : 'text-[#0F172A]'}`} data-id="element-9389" data-mp="17w3em2fgnywo">
                            {tier.name}
                          </button>)}
                      </div>}
                  </div>
                </div>

                {/* Lock Tier Checkbox */}
                <div data-id="element-9390" data-mp="cmq2e41lc4ho">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-9391" data-mp="cn9lho2z8yxa">
                    <div onClick={() => setLockTier(!lockTier)} className={`w-[18px] h-[18px] mt-0.5 rounded border flex items-center justify-center shrink-0 transition-colors ${lockTier ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1] bg-white'}`} data-id="element-9392" data-mp="qdlf5rf7n5qj">
                      {lockTier && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-9393" data-mp="vcfyqvl04ybp">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-9394" data-mp="19bfm7zws376a" />
                        </svg>}
                    </div>
                    <div data-id="element-9395" data-mp="3b32gqbfvpk7i">
                      <div className="flex items-center gap-2" data-id="element-9396" data-mp="3b2962zdsuak3">
                        <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-9397" data-mp="3b4vmzpmky2ez">
                          Lock tier — prevent automatic tier recalculation
                        </span>
                        {hasDowngradeInSelection && <span className="text-[11px] font-medium text-[#0EA5E9] bg-[#F0F9FF] px-1.5 py-0.5 rounded" data-id="element-9398" data-mp="29gnoz88gu5g9">
                            Recommended
                          </span>}
                      </div>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-9399" data-mp="2j5yvcn3yww0g">
                        Locked members will stay in this tier regardless of
                        points balance until manually unlocked
                      </p>
                    </div>
                  </label>
                </div>

                {/* Reason Dropdown */}
                <div data-id="element-9400" data-mp="24f84bha7a0ye">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9401" data-mp="1zi807bwwv89x">
                    Reason <span className="text-[#EF4444]" data-id="element-9402" data-mp="3fwhlfx5uwgwj">*</span>
                  </label>
                  <div className="relative" data-id="element-9403" data-mp="3fyavznjlbl9t">
                    <button type="button" onClick={() => setIsTierReasonDropdownOpen(!isTierReasonDropdownOpen)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-left flex items-center justify-between focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-9404" data-mp="3ay6g9zqiaukn">
                      <span className={tierReasonPreset ? 'text-[#0F172A]' : 'text-[#94A3B8]'} data-id="element-9405" data-mp="3efav8s0qpsm0">
                        {tierReasonPreset || 'Select a reason...'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-[#475569] transition-transform ${isTierReasonDropdownOpen ? 'rotate-180' : ''}`} data-id="element-9406" data-mp="61v9ekx6frbz" />
                    </button>
                    {isTierReasonDropdownOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2E8F0] rounded-[6px] shadow-lg overflow-hidden" data-id="element-9407" data-mp="3sd96hojrl8xa">
                        {REASON_PRESETS.map(reason => <button key={reason} type="button" onClick={() => {
                    setTierReasonPreset(reason);
                    setIsTierReasonDropdownOpen(false);
                    if (reason !== 'Other') {
                      setTierReasonCustom('');
                    }
                  }} className={`w-full px-3 py-2.5 text-[14px] text-left hover:bg-[#F1F5F9] transition-colors ${tierReasonPreset === reason ? 'bg-[#F0F9FF] text-[#0EA5E9] font-medium' : 'text-[#0F172A]'}`} data-id="element-9408" data-mp="owsfk27ovimi">
                            {reason}
                          </button>)}
                      </div>}
                  </div>
                  {tierReasonPreset === 'Other' && <input type="text" value={tierReasonCustom} onChange={e => setTierReasonCustom(e.target.value)} placeholder="Enter custom reason..." className="w-full h-[40px] px-3 mt-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-9409" data-mp="fuzfz94p00q1" />}
                </div>

                {/* Preview Summary & Warnings */}
                {selectedTargetTier && (() => {
              // FILTER-SCOPED MODE: aggregate-only preview
              if (allMatchingSelected) {
                return <div className="space-y-3" data-id="element-9410" data-mp="2dhif1mjf0vna">
                          <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-9411" data-mp="2hkwxejutto3w">
                            <p className="text-[13px] font-medium text-[#0F172A]" data-id="element-9412" data-mp="2hiu5fua45kwj">
                              <span className="font-semibold" data-id="element-9413" data-mp="2mhnmfwolzu1g">
                                {TOTAL_MATCHING_COUNT.toLocaleString()}
                              </span>{' '}
                              members matching the current filter will be moved
                              to{' '}
                              <span className="font-semibold" data-id="element-9414" data-mp="1ysuk9ihe8pye">
                                {selectedTargetTier}
                              </span>
                            </p>
                          </div>
                          <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-9415" data-mp="163nogr9bqegu">
                            <AlertTriangle className="w-4 h-4 text-[#D97706] mt-0.5 shrink-0" data-id="element-9416" data-mp="3dqqjpswplnpg" />
                            <p className="text-[13px] text-[#92400E]" data-id="element-9417" data-mp="38sozh4njrice">
                              Some members may be downgraded from their current
                              tier. Per-member preview isn't available when
                              applying to all matching members — review
                              individual changes in the Activity Log after the
                              action completes.
                            </p>
                          </div>
                        </div>;
              }
              const targetMembers = mockMembers.filter(m => selectedMembers.has(m.id));
              const memberCount = targetMembers.length;
              const targetRank = getTierRank(selectedTargetTier);
              const downgradedMembers = targetMembers.filter(m => getTierRank(m.tier) > targetRank);
              return <div className="space-y-3" data-id="element-9418" data-mp="274eaggmljb2m">
                        {/* Summary */}
                        <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-9419" data-mp="1wzem0x5cwhxg">
                          <p className="text-[13px] font-medium text-[#0F172A]" data-id="element-9420" data-mp="20zeywdct4l2e">
                            <span className="font-semibold" data-id="element-9421" data-mp="1n9lce1orycvu">{memberCount}</span>{' '}
                            member{memberCount !== 1 ? 's' : ''} will be moved
                            from various tiers to{' '}
                            <span className="font-semibold" data-id="element-9422" data-mp="1d9udlnt6lbpy">
                              {selectedTargetTier}
                            </span>
                          </p>
                        </div>

                        {/* Downgrade warning — overrides permanent-tier policy */}
                        {downgradedMembers.length > 0 && <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-[6px] px-4 py-3" data-id="element-9423" data-mp="199tys2266yid">
                            <div className="flex items-start gap-2.5 mb-2" data-id="element-9424" data-mp="2yp04os61ggkm">
                              <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" data-id="element-9425" data-mp="33m0e9d23k1gh" />
                              <div className="flex-1" data-id="element-9426" data-mp="319g24xewowbm">
                                <p className="text-[13px] font-semibold text-[#991B1B] mb-1" data-id="element-9427" data-mp="2x72zwizdbgjl">
                                  This overrides your program's permanent-tier
                                  policy
                                </p>
                                <p className="text-[13px] text-[#991B1B]" data-id="element-9428" data-mp="2nagcpnjkkdee">
                                  <span className="font-semibold" data-id="element-9429" data-mp="3kwx98qkhamz3">
                                    {downgradedMembers.length}
                                  </span>{' '}
                                  of{' '}
                                  <span className="font-semibold" data-id="element-9430" data-mp="3b5jc2pg01z94">
                                    {memberCount}
                                  </span>{' '}
                                  member
                                  {downgradedMembers.length !== 1 ? 's' : ''}{' '}
                                  would be moved to a LOWER tier than they
                                  currently hold. Tier lock has been enabled by
                                  default to prevent automatic re-evaluation
                                  back to their original tier.
                                </p>
                              </div>
                            </div>
                          </div>}
                      </div>;
            })()}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-9431" data-mp="t0i768gdjwi3">
                <Button variant="ghost" size="md" onClick={() => setIsTierModalOpen(false)} data-id="element-9432" data-mp="xybex5xbsykh">
                  Cancel
                </Button>
                <Button variant="primary" size="md" disabled={!selectedTargetTier || !tierReasonPreset} onClick={() => {
              const count = allMatchingSelected ? TOTAL_MATCHING_COUNT : selectedMembers.size;
              let downgradeNote = '';
              if (!allMatchingSelected && selectedTargetTier) {
                const targetMembers = mockMembers.filter(m => selectedMembers.has(m.id));
                const targetRank = getTierRank(selectedTargetTier);
                const downgrades = targetMembers.filter(m => getTierRank(m.tier) > targetRank).length;
                if (downgrades > 0) {
                  downgradeNote = ` (${downgrades} downgraded, tier locked)`;
                }
              }
              const reason = tierReasonPreset === 'Other' ? tierReasonCustom.trim() : tierReasonPreset;
              // Live audit (pass 3): bulk tier change
              logBulkAudit(count, {
                eventType: 'Tier',
                activity: `Bulk tier change — ${count.toLocaleString()} members → ${selectedTargetTier}${downgradeNote}`,
                changes: [{
                  field: 'Tier',
                  before: '—',
                  after: selectedTargetTier
                }, {
                  field: 'Reason',
                  before: '—',
                  after: reason
                }, ...(lockTier ? [{
                  field: 'Tier Lock',
                  before: 'Unlocked',
                  after: 'Locked'
                }] : [])]
              });
              setToasts(prev => [...prev, {
                id: `t-${Date.now()}`,
                type: 'success',
                title: `Moved ${count.toLocaleString()} ${count === 1 ? 'member' : 'members'} to ${selectedTargetTier}${downgradeNote}`,
                duration: 5000,
                onClose: removeToast
              }]);
              setIsTierModalOpen(false);
              setSelectedMembers(new Set());
              setAllMatchingSelected(false);
            }} data-id="element-9433" data-mp="17po5lu7e1iox">
                  Apply Change
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Change Loyalty Status Modal */}
      {isStatusModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsStatusModalOpen(false)} data-id="element-9434" data-mp="2aj4fbd05htz6" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-9435" data-mp="18m4i1k2ytmit">
            <div className="bg-white rounded-lg shadow-xl w-[420px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-9436" data-mp="1dk62o9g1htwe">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-9437" data-mp="1ml44qm6x3rcc">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-9438" data-mp="putvtg86pxx9">
                  Update status for {bulkScopeLabel}
                </h2>
                <IconButton icon={<X className="w-5 h-5" data-id="element-9440" data-mp="uu6erpvxqkuk" />} label="Close modal" variant="ghost" onClick={() => setIsStatusModalOpen(false)} data-id="element-9439" data-mp="uupx37phzb94" />
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 space-y-5 overflow-y-auto" data-id="element-9441" data-mp="13n1nwuoix0pm">
                <p className="text-sm text-[#475569]" data-id="element-9442" data-mp="74vletgwzsqg">
                  This will apply to{' '}
                  <span className="font-semibold text-[#0F172A]" data-id="element-9443" data-mp="b5fi5vxksuc2">
                    {bulkScopeLabel}
                  </span>
                  .
                </p>

                {/* Status Action Toggle */}
                <div data-id="element-9444" data-mp="3ushguishywnu">
                  <label className="block text-xs font-medium text-[#475569] mb-2" data-id="element-9445" data-mp="3uu0t7yb18nkc">
                    Action
                  </label>
                  <div className="flex gap-4" data-id="element-9446" data-mp="3qsywbo4l8nlj">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-9447" data-mp="3lqzskd4xroo2">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${statusAction === 'pause' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setStatusAction('pause')} data-id="element-9448" data-mp="3gthszdct6lsq">
                        {statusAction === 'pause' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-9449" data-mp="373ep2i45fnfm" />}
                      </div>
                      <span className="text-sm text-[#0F172A]" data-id="element-9450" data-mp="333e7wppo0y4v">
                        Pause Loyalty
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-9451" data-mp="2t33wnan6y0sc">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${statusAction === 'resume' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setStatusAction('resume')} data-id="element-9452" data-mp="2jg1f1vjpps13">
                        {statusAction === 'resume' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-9453" data-mp="29j51hbeyifpo" />}
                      </div>
                      <span className="text-sm text-[#0F172A]" data-id="element-9454" data-mp="2egx4p1l1xo96">
                        Resume Loyalty
                      </span>
                    </label>
                  </div>
                </div>

                {/* Info Notice & Skip Notice */}
                {(() => {
              // FILTER-SCOPED MODE: aggregate-only copy, no per-member skip counts
              if (allMatchingSelected) {
                return <div className="space-y-3" data-id="element-9455" data-mp="2xdg3oy255vs0">
                        <div className="bg-[#DBEAFE] border border-[#BFDBFE] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-9456" data-mp="3l4v9j0t4mv80">
                          <Info className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" data-id="element-9457" data-mp="3pxeoa13jvwee" />
                          <p className="text-[13px] text-[#1E40AF]" data-id="element-9458" data-mp="1efjwflu8fl2v">
                            {statusAction === 'pause' ? 'Active members will be paused. Already-paused and expired members will be skipped automatically.' : 'Paused and expired members will be resumed. Already-active members will be skipped automatically.'}
                          </p>
                        </div>
                        <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-9459" data-mp="vj1wih58cevj">
                          <p className="text-[13px] text-[#475569]" data-id="element-9460" data-mp="qjfjo8tbd0n9">
                            Action will apply to{' '}
                            <span className="font-semibold text-[#0F172A]" data-id="element-9461" data-mp="14jq8z7ez676b">
                              {TOTAL_MATCHING_COUNT.toLocaleString()}
                            </span>{' '}
                            members matching the current filter. Per-member skip
                            counts will be shown after the action completes.
                          </p>
                        </div>
                      </div>;
              }
              const targetMembers = mockMembers.filter(m => selectedMembers.has(m.id));
              const totalSelected = targetMembers.length;
              const alreadyPaused = targetMembers.filter(m => m.status === 'Paused').length;
              const alreadyActive = targetMembers.filter(m => m.status === 'Active').length;
              const expiredMembers = targetMembers.filter(m => m.status === 'Expired').length;
              return <div className="space-y-3" data-id="element-9462" data-mp="3u6mnq1yg5qjt">
                      {/* Info notice */}
                      <div className="bg-[#DBEAFE] border border-[#BFDBFE] rounded-[6px] px-4 py-3 flex items-start gap-2.5" data-id="element-9463" data-mp="2sgyu3l13988">
                        <Info className="w-4 h-4 text-[#2563EB] mt-0.5 shrink-0" data-id="element-9464" data-mp="7rjwv42lcf6p" />
                        <p className="text-[13px] text-[#1E40AF]" data-id="element-9465" data-mp="cqwr49vskojb">
                          {statusAction === 'pause' ? 'Paused members will stop earning points on purchases but will retain their current balance and tier' : 'Members will begin earning points again on their next qualifying purchase'}
                        </p>
                      </div>

                      {/* Skip notices — all applicable reasons render */}
                      {statusAction === 'pause' && alreadyPaused > 0 && <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-9466" data-mp="1na9f4gpcvmt1">
                          <p className="text-[13px] text-[#475569]" data-id="element-9467" data-mp="1ncc9fcxjk23k">
                            <span className="font-semibold text-[#0F172A]" data-id="element-9468" data-mp="2kwoxdkplwohn">
                              {alreadyPaused}
                            </span>{' '}
                            of{' '}
                            <span className="font-semibold text-[#0F172A]" data-id="element-9469" data-mp="2b6vs11g255lu">
                              {totalSelected}
                            </span>{' '}
                            selected members are already paused and will be
                            skipped
                          </p>
                        </div>}
                      {statusAction === 'pause' && expiredMembers > 0 && <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-9470" data-mp="x1a9rx1cowah">
                          <p className="text-[13px] text-[#475569]" data-id="element-9471" data-mp="11ztxo2zi8i5h">
                            <span className="font-semibold text-[#0F172A]" data-id="element-9472" data-mp="12151g2sy9pwl">
                              {expiredMembers}
                            </span>{' '}
                            of{' '}
                            <span className="font-semibold text-[#0F172A]" data-id="element-9473" data-mp="s760v88iye6r">
                              {totalSelected}
                            </span>{' '}
                            selected members are expired and cannot be paused
                            (will be skipped)
                          </p>
                        </div>}
                      {statusAction === 'resume' && alreadyActive > 0 && <div className="bg-[#F1F5F9] rounded-[6px] px-4 py-3" data-id="element-9474" data-mp="9cr47p9oqxm3">
                          <p className="text-[13px] text-[#475569]" data-id="element-9475" data-mp="1yq4d7rsqt9tj">
                            <span className="font-semibold text-[#0F172A]" data-id="element-9476" data-mp="23nmldfyxrl0h">
                              {alreadyActive}
                            </span>{' '}
                            of{' '}
                            <span className="font-semibold text-[#0F172A]" data-id="element-9477" data-mp="3j8eutfylart4">
                              {totalSelected}
                            </span>{' '}
                            selected members are already active and will be
                            skipped
                          </p>
                        </div>}
                    </div>;
            })()}

                {/* Reason (optional) */}
                <div data-id="element-9478" data-mp="5rxnogjxz1id">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9479" data-mp="5sh23nadv7rn">
                    Reason <span className="text-[#94A3B8]" data-id="element-9480" data-mp="kp85e7n6h3mj">(optional)</span>
                  </label>
                  <input type="text" value={statusReason} onChange={e => setStatusReason(e.target.value)} placeholder="e.g. Seasonal pause" className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-9481" data-mp="frogmdsut8gc" />
                </div>

                {/* Notify Members Checkbox */}
                <div data-id="element-9482" data-mp="25enk4azcuybq">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-9483" data-mp="25g8dhx9xob80">
                    <div onClick={() => setNotifyMembers(!notifyMembers)} className={`w-[18px] h-[18px] mt-0.5 rounded border flex items-center justify-center shrink-0 transition-colors ${notifyMembers ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1] bg-white'}`} data-id="element-9484" data-mp="2ae9xckf6oekj">
                      {notifyMembers && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-9485" data-mp="1mnwglq21u259">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-9486" data-mp="1qprf6msli1fc" />
                        </svg>}
                    </div>
                    <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-9487" data-mp="3256alm55ol8c">
                      Notify members by email
                    </span>
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-9488" data-mp="mhiuf5o7kktv">
                <Button variant="ghost" size="md" onClick={() => setIsStatusModalOpen(false)} data-id="element-9489" data-mp="hny6y7k8r98x">
                  Cancel
                </Button>
                <Button variant="primary" size="md" onClick={() => {
              const targetMembers = allMatchingSelected ? [] : mockMembers.filter(m => selectedMembers.has(m.id));
              const statusCount = allMatchingSelected ? TOTAL_MATCHING_COUNT : selectedMembers.size;
              let toastTitle = '';
              if (allMatchingSelected) {
                const verb = statusAction === 'pause' ? 'Paused' : 'Resumed';
                toastTitle = `${verb} eligible members across ${TOTAL_MATCHING_COUNT.toLocaleString()} matching members. Check Activity Log for per-member results.`;
              } else {
                if (statusAction === 'pause') {
                  const willPause = targetMembers.filter(m => m.status === 'Active').length;
                  const alreadyPaused = targetMembers.filter(m => m.status === 'Paused').length;
                  const expired = targetMembers.filter(m => m.status === 'Expired').length;
                  const skipNotes: string[] = [];
                  if (alreadyPaused > 0) skipNotes.push(`${alreadyPaused} already paused`);
                  if (expired > 0) skipNotes.push(`${expired} expired`);
                  const skipText = skipNotes.length > 0 ? ` (${skipNotes.join(', ')} skipped)` : '';
                  toastTitle = `Paused ${willPause} ${willPause === 1 ? 'member' : 'members'}${skipText}`;
                } else {
                  const willResume = targetMembers.filter(m => m.status === 'Paused' || m.status === 'Expired').length;
                  const alreadyActive = targetMembers.filter(m => m.status === 'Active').length;
                  const skipText = alreadyActive > 0 ? ` (${alreadyActive} already active, skipped)` : '';
                  toastTitle = `Resumed ${willResume} ${willResume === 1 ? 'member' : 'members'}${skipText}`;
                }
              }
              // Live audit (pass 3): bulk status change
              logBulkAudit(statusCount, {
                eventType: 'Loyalty Status',
                activity: `Bulk status change — ${statusCount.toLocaleString()} members ${statusAction === 'pause' ? 'paused' : 'resumed'}`,
                changes: [{
                  field: 'Loyalty Status',
                  before: '—',
                  after: statusAction === 'pause' ? 'Paused' : 'Active'
                }, ...(statusReason.trim() ? [{
                  field: 'Reason',
                  before: '—',
                  after: statusReason.trim()
                }] : [])]
              });
              setToasts(prev => [...prev, {
                id: `t-${Date.now()}`,
                type: 'success',
                title: toastTitle,
                duration: 5000,
                onClose: removeToast
              }]);
              setIsStatusModalOpen(false);
              setSelectedMembers(new Set());
              setAllMatchingSelected(false);
            }} data-id="element-9490" data-mp="7rbo1obh6s9j">
                  Apply Change
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Export Modal */}
      {isExportModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsExportModalOpen(false)} data-id="element-9491" data-mp="1i6iivp0ov81h" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-9492" data-mp="1wuvgeqffcyy3">
            <div className="bg-white rounded-lg shadow-xl w-[460px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-9493" data-mp="1rw22wy0517xx">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-9494" data-mp="25tqq4chogr65">
                <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-9495" data-mp="20uvuub2ixa55">
                  Export {exportScopeCount.toLocaleString()} members
                </h2>
                <IconButton icon={<X className="w-5 h-5" data-id="element-9497" data-mp="2ph3d4q0y9320" />} label="Close modal" variant="ghost" onClick={() => setIsExportModalOpen(false)} data-id="element-9496" data-mp="2pgacm6sl39sb" />
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 space-y-5 overflow-y-auto" data-id="element-9498" data-mp="2fi4bvsiik9p1">
                <p className="text-sm text-[#475569]" data-id="element-9499" data-mp="21riqnx5l7nnz">
                  Exporting{' '}
                  <span className="font-semibold text-[#0F172A]" data-id="element-9500" data-mp="26ih8x2a4yvtv">
                    {exportScopeDescriptor}
                  </span>
                  .
                </p>

                {/* Columns picker */}
                <div data-id="element-9501" data-mp="1wsej1ayb9xzr">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-9502" data-mp="1wtpln7g0szpa">
                    Columns to include
                  </label>
                  <div className="space-y-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-[6px] p-3" data-id="element-9503" data-mp="1ns9kzrf0cby9">
                    {[{
                  key: 'name',
                  label: 'Member name'
                }, {
                  key: 'email',
                  label: 'Email'
                }, {
                  key: 'tier',
                  label: 'Tier'
                }, {
                  key: 'tier_locked',
                  label: 'Tier locked flag'
                }, {
                  key: 'points',
                  label: 'Current points'
                }, {
                  key: 'lifetimePoints',
                  label: 'Lifetime points'
                }, {
                  key: 'lastActivity',
                  label: 'Last activity (ISO timestamp)'
                }, {
                  key: 'status',
                  label: 'Status'
                }].map(({
                  key,
                  label
                }) => {
                  const isChecked = exportColumns.has(key);
                  return <label key={key} className="flex items-center gap-3 cursor-pointer" data-id="element-9504" data-mp="2dgddqtlpjvy0">
                          <div onClick={() => {
                      const next = new Set(exportColumns);
                      if (isChecked) {
                        next.delete(key);
                      } else {
                        next.add(key);
                      }
                      setExportColumns(next);
                    }} className={`w-[18px] h-[18px] rounded border flex items-center justify-center shrink-0 transition-colors ${isChecked ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1] bg-white'}`} data-id="element-9505" data-mp="3vdqidf6u4kta">
                            {isChecked && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-9506" data-mp="3hfa4936n7s14">
                                <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-9507" data-mp="3lgvlf0q7msh4" />
                              </svg>}
                          </div>
                          <span className="text-[13px] text-[#0F172A]" data-id="element-9508" data-mp="10qwgl04goziq">
                            {label}
                          </span>
                        </label>;
                })}
                  </div>
                  <p className="text-[12px] text-[#64748B] mt-1" data-id="element-9509" data-mp="2xp6cdl2kw35k">
                    {exportColumns.size} of 8 columns selected
                  </p>
                </div>

                {/* Email destination */}
                <div data-id="element-9510" data-mp="36pcnj0at0r1k">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9511" data-mp="36qw64xjxne6i">
                    Send CSV to <span className="text-[#EF4444]" data-id="element-9512" data-mp="2j1bhby13lmpb">*</span>
                  </label>
                  <input type="email" value={exportEmail} onChange={e => setExportEmail(e.target.value)} placeholder="you@example.com" className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-9513" data-mp="2e1yngtbt7fd8" />
                  <p className="text-[12px] text-[#64748B] mt-1" data-id="element-9514" data-mp="1vaz6ofegxbv1">
                    We'll email you a download link when the export is ready.
                    Large exports may take a few minutes.
                  </p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3" data-id="element-9515" data-mp="1s8gj3d0twjq">
                <Button variant="ghost" size="md" onClick={() => setIsExportModalOpen(false)} data-id="element-9516" data-mp="5t294a1erziy">
                  Cancel
                </Button>
                <Button variant="primary" size="md" disabled={exportColumns.size === 0 || exportEmail.trim() === ''} onClick={() => {
              setToasts(prev => [...prev, {
                id: `t-${Date.now()}`,
                type: 'success',
                title: `Export queued — we'll email ${exportEmail} when ready (${exportScopeCount.toLocaleString()} members, ${exportColumns.size} columns)`,
                duration: 6000,
                onClose: removeToast
              }]);
              setIsExportModalOpen(false);
            }} data-id="element-9517" data-mp="tjxv2qj6o0ia">
                  Email me the CSV
                </Button>
              </div>
            </div>
          </div>
        </>}

      {/* Toasts */}
      {toasts.map(toast => <Toast key={toast.id} {...toast} onClose={removeToast} data-id="element-9518" data-mp="t8ppq2z71d3" />)}
    </div>;
}